import { useLocation } from 'react-router-dom'
import { CAPACITY, CURRY_CATEGORY, DRINKS_CATEGORY, SCREEN_OFFSET_MS } from '../config/board'
import { heroSlides, productsInCategory, variantColumns } from '../lib/catalog'
import { PROGRAMS, ScreenId, atOverride, hhmmNow, pickProgram } from '../lib/programs'
import { useLiveCatalog } from '../lib/useLiveCatalog'
import { useNow } from '../lib/useNow'
import { ColumnTitle } from './ColumnTitle'
import { DebugOverlay } from './DebugOverlay'
import { DrinksColumn } from './DrinksColumn'
import { Header } from './Header'
import { HeroPanel } from './HeroPanel'
import { HowItWorks } from './HowItWorks'
import { LineRow } from './LineRow'
import { Paged } from './Paged'
import { ProductRow } from './ProductRow'
import { StaleStamp } from './StaleStamp'

// One wall: the hero panel on the left, the main list on the right, and on screen 2
// the two columns under it. What each screen shows is the program's decision, read
// from the clock on every poll tick (`?at=HH:MM` previews another moment).
export function Board({ screen }: { screen: ScreenId }) {
  const { categories, products, bundle, isFrom, optionsSettled, lastSuccessAt, stale } = useLiveCatalog()
  const now = useNow()
  const at = atOverride(useLocation().search)
  const program = pickProgram(PROGRAMS, at || hhmmNow(now))
  const config = program.screens[screen]

  const main = productsInCategory(products, config.main)
  const columns = variantColumns(main)
  // Titles are DB text: the category as the API spells it, not as the config does.
  const titleOf = (name: string) => {
    const match = categories.filter((c) => c.name === name)[0]
    return match ? match.name : name
  }

  // The Meal Deal hangs off a curry variant, so it belongs on the curry screen.
  const slides = heroSlides(main, config.main === CURRY_CATEGORY ? bundle : null, isFrom)
  const extras = config.extra || []

  return (
    <div
      className="w-[1920px] h-[1080px] px-[72px] py-[48px] flex space-x-[96px] bg-brand-parchment text-secondary-800 font-secondary"
      data-ready={products.length > 0 && optionsSettled}
    >
      <HeroPanel slides={slides} offsetMs={screen === '2' ? SCREEN_OFFSET_MS : 0} />
      <div className="relative flex-1 flex flex-col min-w-0">
        {main.length > 0 ? (
          <>
            <Header title={titleOf(config.main)} columns={columns} />
            <div className={screen === '1' ? 'flex flex-col flex-1 min-h-0' : 'flex flex-col'}>
              <Paged capacity={CAPACITY.main} fill={screen === '1'}>
                {main.map((p) =>
                  screen === '1' ? (
                    <ProductRow key={p.id} product={p} isFrom={isFrom} />
                  ) : (
                    <LineRow
                      key={p.id}
                      product={p}
                      nameSize={40}
                      markerSize={28}
                      priceSize={40}
                      pad={10}
                      isFrom={isFrom}
                    />
                  ),
                )}
              </Paged>
            </div>
          </>
        ) : null}

        {config.footer === 'how-it-works' ? <HowItWorks sizes={columns} bundle={bundle} /> : null}

        {extras.length > 0 ? (
          <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-[48px] flex-1 min-h-0 pt-[24px]">
            {extras.map((name) => {
              const items = productsInCategory(products, name)
              // An empty category collapses: the wall never shows a heading with
              // nothing under it.
              if (items.length === 0) return null
              if (name === DRINKS_CATEGORY) {
                return <DrinksColumn key={name} title={titleOf(name)} products={items} isFrom={isFrom} />
              }
              return (
                <div key={name} className="flex flex-col">
                  <ColumnTitle>{titleOf(name)}</ColumnTitle>
                  <Paged capacity={CAPACITY.extras}>
                    {items.map((p) => (
                      <LineRow
                        key={p.id}
                        product={p}
                        nameSize={34}
                        markerSize={26}
                        priceSize={34}
                        pad={8}
                        markers="dietary"
                        truncateName={false}
                        isFrom={isFrom}
                      />
                    ))}
                  </Paged>
                </div>
              )
            })}
          </div>
        ) : null}

        <StaleStamp at={stale ? lastSuccessAt : null} />
        {/* Inside the column, not the spaced root: `space-x` would push its fixed frame. */}
        <DebugOverlay program={program.id} lastSuccessAt={lastSuccessAt} />
      </div>
    </div>
  )
}
